import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {IntersectionObserver | undefined} */
  #pdaVisibilityObserver;

  /**
   * PDA (QA sidecart 18/07) : le connectedCallback parent calcule bien l'état des
   * boutons, mais le MORPH post-chargement du drawer (sync panier) retire le
   * `disabled` sans re-déclencher connectedCallback → le « − » d'un item déjà à
   * qty min restait actif à l'ouverture à froid (prouvé au flux réel :
   * updateButtonStates() manuel corrige). Re-synchronisation quand le stepper
   * devient VISIBLE (= ouverture du drawer, seul moment où l'état se voit).
   * ⚠️ PAS de MutationObserver ici : réagir aux mutations pendant que le moteur
   * de morph écrit déclenche une guerre de mutations (renderer gelé, vécu 18/07).
   */
  connectedCallback() {
    super.connectedCallback();
    this.#pdaVisibilityObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.updateButtonStates();
    });
    this.#pdaVisibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#pdaVisibilityObserver?.disconnect();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
